import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { FictoanButtonProps } from "../../lib/fictoan-schema";
import { cn, mapFictoanButtonProps } from "../../lib/utils";

const buttonVariants = cva(
    "setu:inline-flex setu:items-center setu:justify-center setu:gap-2 setu:whitespace-nowrap setu:rounded-md setu:!text-sm setu:font-medium setu:ring-offset-background setu:transition-colors setu:focus-visible:outline-hidden focus-visible:ring-2 setu:focus-visible:ring-ring setu:focus-visible:ring-offset-2 setu:disabled:pointer-events-none setu:disabled:opacity-50 setu:[&_svg]:pointer-events-none [&_svg]:size-4 setu:[&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default: "setu:!bg-primary setu:!text-primary-foreground setu:hover:!bg-primary/90",
                destructive: "setu:!bg-destructive setu:!text-white setu:hover:!bg-destructive/90",
                outline:
                    "setu:!text-foreground setu:!border setu:!border-input setu:!bg-background setu:hover:!bg-accent setu:hover:!text-accent-foreground",
                secondary: "setu:!bg-secondary setu:!text-secondary-foreground setu:hover:!bg-secondary/80",
                ghost: "setu:!text-foreground setu:hover:!bg-accent setu:hover:!text-accent-foreground",
                link: "setu:text-primary setu:!underline-offset-4 setu:hover:!underline",
                // Additional variants for Fictoan compatibility
                success: "setu:!bg-green-600 setu:!text-white setu:hover:!bg-green-700",
                warning: "setu:!bg-orange-500 setu:!text-white setu:hover:!bg-orange-600",
                info: "setu:!bg-blue-600 setu:!text-white setu:hover:!bg-blue-700",
            },
            size: {
                default: "setu:!h-9 setu:px-4 setu:!py-2 setu:has-[>svg]:px-3",
                sm: "setu:!h-8 setu:rounded-md setu:gap-1.5 setu:px-3 setu:has-[>svg]:px-2.5",
                lg: "setu:!h-10 setu:rounded-md setu:px-6 setu:has-[>svg]:px-4",
                icon: "setu:!size-9",
                "icon-sm": "setu:!size-8",
                "icon-lg": "setu:!size-10",
            },
            fullWidth: {
                true: "setu:!w-full",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        Omit<VariantProps<typeof buttonVariants>, "size"> {
    asChild?: boolean;
    loading?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    fullWidth?: boolean;
    // Fictoan compatibility props
    size?: FictoanButtonProps["size"];
    colour?: FictoanButtonProps["colour"];
    kind?: FictoanButtonProps["kind"];
    shadow?: FictoanButtonProps["shadow"];
    isLoading?: FictoanButtonProps["isLoading"];
    isFullWidth?: FictoanButtonProps["isFullWidth"];
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            fullWidth,
            asChild = false,
            loading = false,
            startIcon,
            endIcon,
            disabled,
            children,
            // Fictoan compatibility props
            colour,
            kind,
            shadow,
            isLoading,
            isFullWidth,
            ...props
        },
        ref
    ) => {
        // Map Fictoan props to shadcn/ui props (only if Fictoan props are provided)
        const mappedProps = mapFictoanButtonProps({
            colour,
            kind,
            size,
            shadow,
            isLoading,
            isFullWidth,
            disabled,
        });

        // Use shadcn props first, fallback to mapped Fictoan props
        const finalVariant = variant || mappedProps.variant;
        // Always use mappedProps.size which handles both Fictoan and shadcn sizes
        const finalSize = mappedProps.size || "default";
        const finalFullWidth = fullWidth || mappedProps.fullWidth;
        const finalLoading = loading || mappedProps.loading;
        const finalDisabled = disabled || mappedProps.disabled;

        const Comp = asChild ? Slot : "button";

        return (
            <Comp
                className={cn(
                    buttonVariants({
                        variant: finalVariant as VariantProps<typeof buttonVariants>["variant"],
                        size: finalSize as VariantProps<typeof buttonVariants>["size"],
                        fullWidth: finalFullWidth,
                    }),
                    mappedProps.className, // For shadow classes
                    className
                )}
                ref={ref}
                disabled={finalDisabled || finalLoading}
                {...props}
            >
                {finalLoading && (
                    <svg
                        className="setu:mr-2 setu:h-4 setu:w-4 setu:animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="setu:opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="setu:opacity-75"
                            fill="currentColor"
                            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                )}
                {!finalLoading && startIcon && <span className="mr-1">{startIcon}</span>}
                {children}
                {!finalLoading && endIcon && <span className="ml-1">{endIcon}</span>}
            </Comp>
        );
    }
);
Button.displayName = "Button";

// Fictoan compatibility wrapper
const FictoanButton = React.forwardRef<HTMLButtonElement, FictoanButtonProps>((props, ref) => {
    const mappedProps = mapFictoanButtonProps(props);

    return (
        <Button
            ref={ref}
            variant={mappedProps.variant as ButtonProps["variant"]}
            size={mappedProps.size as ButtonProps["size"]}
            className={mappedProps.className}
            loading={mappedProps.loading}
            fullWidth={mappedProps.fullWidth}
            disabled={mappedProps.disabled}
            {...props}
        />
    );
});
FictoanButton.displayName = "FictoanButton";

export { Button, buttonVariants, FictoanButton };
